import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Container, Navbar, Nav, NavDropdown, Button, Badge } from 'react-bootstrap';
import { Sun, Moon, LogOut } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../context/PermissionsContext';
import { usePendingApprovals } from '../context/PendingApprovalsContext';

// The pages each entry covers, so an entry stays highlighted on the detail and report pages under it
const SECTIONS = {
  companies: ['/companies', '/offices'],
  crm: ['/crm'],
  users: ['/user-search', '/users'],
  approvals: ['/approvals'],
  reports: ['/reports', '/user-signups', '/unregistered-user-visits', '/system-report', '/data-insights'],
  tools: ['/monitoring', '/server-health', '/logs', '/cache-controls', '/marketing-site'],
};

const inSection = (pathname, prefixes) =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const Navigation = () => {
  const { accounts, instance } = useMsal();
  const { theme, toggleTheme } = useTheme();
  const { role, loading } = usePermissions();
  const { total: pendingApprovals } = usePendingApprovals();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);

  // Below xl the links live in a collapsed menu, which closes once a link has been followed
  useEffect(() => {
    setExpanded(false);
  }, [pathname]);

  const handleLogout = () => {
    // The popup ends on the redirect bridge (postLogoutRedirectUri in index.jsx), which closes it
    instance.logoutPopup();
  };

  // A top-level link, active on every page of its section
  const sectionLink = (section, to, label) => {
    const active = inSection(pathname, SECTIONS[section]);
    return (
      <Nav.Link as={Link} to={to} active={active} aria-current={active ? 'page' : undefined}>
        {label}
      </Nav.Link>
    );
  };

  // A Tools menu entry; the open page is marked for screen readers and in bold
  const toolLink = (to, label) => {
    const current = inSection(pathname, [to]);
    return (
      <NavDropdown.Item
        as={Link}
        to={to}
        aria-current={current ? 'page' : undefined}
        className={current ? 'fw-semibold' : undefined}
      >
        {label}
      </NavDropdown.Item>
    );
  };

  const homeActive = pathname === '/';
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Navbar expand="xl" sticky="top" className="navbar-custom" expanded={expanded} onToggle={setExpanded}>
      <Container>
        <Navbar.Brand as={Link} to="/">
          <strong>Team Today Admin</strong>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" aria-label="Menu" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <Nav.Link as={Link} to="/" active={homeActive} aria-current={homeActive ? 'page' : undefined}>
              Home
            </Nav.Link>
            {sectionLink('companies', '/companies', 'Companies')}
            {sectionLink('crm', '/crm', 'CRM')}
            {sectionLink('users', '/user-search', 'Users')}
            {sectionLink('approvals', '/approvals', (
              <>
                Approvals
                {pendingApprovals > 0 && (
                  <Badge bg="warning" pill className="ms-1">
                    {pendingApprovals}<span className="visually-hidden"> waiting</span>
                  </Badge>
                )}
              </>
            ))}
            {sectionLink('reports', '/reports', 'Reports')}
            {/* Inside the collapsed menu below xl the dropdown opens in place, under its toggle */}
            <NavDropdown title="Tools" id="nav-tools" active={inSection(pathname, SECTIONS.tools)}>
              {toolLink('/monitoring', 'Monitoring')}
              {toolLink('/server-health', 'Server health')}
              {!loading && role === 'ADMIN' && toolLink('/logs', 'Logs')}
              {toolLink('/cache-controls', 'Cache controls')}
              {toolLink('/marketing-site', 'Marketing site')}
            </NavDropdown>
          </Nav>
          <Nav className="navbar-account">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleTheme}
              className="me-3 navbar-account-theme"
              title={`Switch to ${nextTheme} mode`}
              aria-label={`Switch to ${nextTheme} mode`}
            >
              {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
              <span className="d-xl-none ms-2">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </Button>
            {!loading && role === 'VIEWER' && (
              <Badge bg="secondary" className="me-3 align-self-center navbar-account-badge">
                View only
              </Badge>
            )}
            <span className="me-3 text-muted align-self-center navbar-account-name">
              {accounts[0]?.name}
            </span>
            <div className="navbar-account-break d-xl-none" />
            <Nav.Link
              as={Link}
              to="/profile"
              active={pathname === '/profile'}
              aria-current={pathname === '/profile' ? 'page' : undefined}
              className="me-2 navbar-account-profile"
            >
              Profile
            </Nav.Link>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleLogout}
              className="d-inline-flex align-items-center navbar-account-logout"
            >
              <LogOut size={14} className="me-1" aria-hidden="true" />
              Sign out
            </Button>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Navigation;
